import { Fragment, useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'
import {
  batchesApi,
  BATCH_ANOMALY_LABELS,
  BATCH_PRIORITY_LABELS,
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  type Batch,
  type BatchChecks,
  type BatchCriteria,
  type BatchItem,
  type BatchPriority,
  type BatchRule,
  type BatchRuleApplyResult,
  type BatchRuleInput,
  type BatchRuleTargetMode,
  type BatchSort,
  type BatchStatus,
} from '../api/batches'
import { fetchDepartments, fetchOperators, type Department, type Operator } from '../api/catalog'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { UserIdsPicker } from '../components/UserIdsPicker'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button, Callout, Card, CardActions, CardContent, CardHeader, Checkbox, DataTable, DataTableEmptyRow, Dialog,
  FilterBar, FormGrid, IconButton, LoadingState, Page, PageHeader, Pagination, Select, SortHeader, StatusPill, Tabs,
  TextField, type PillTone, useConfirm,
} from '../components/ui'

/** F-100: avviso dopo una clonazione. */
const CLONE_NOTICE = 'Copia creata: è inattiva (in bozza). Controllala e attivala quando è pronta.'

const PAGE_SIZE = 25

/** Colore Bootstrap dei dati (api/batches) -> tono della StatusPill. */
function pillTone(color: string | undefined): PillTone {
  switch (color) {
    case 'success': return 'success'
    case 'warning': return 'warning'
    case 'danger': return 'error'
    case 'info': return 'info'
    case 'primary': return 'primary'
    default: return 'neutral'
  }
}

function fmtDateTime(s: string | undefined | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT')
}

// ───────────────────────────────────────────────────────────────────────────────────────────────
// F-078 (REQ-008-003): criteri di porzionamento — moduli condivisi dal "Nuovo lotto" e dalle regole.
// ───────────────────────────────────────────────────────────────────────────────────────────────

interface CriteriaForm {
  departmentId: string
  productId: string
  customerSegment: string
  contractType: string
  periodFrom: string
  periodTo: string
  priority: BatchPriority
  contestId: string
  contestLabel: string
  maxItems: string
  conventionFieldCode: string
  conventionValue: string
  valueMin: string
  valueMax: string
}

const EMPTY_CRITERIA: CriteriaForm = {
  departmentId: '',
  productId: '',
  customerSegment: '',
  contractType: '',
  periodFrom: '',
  periodTo: '',
  priority: 'Normale',
  contestId: '',
  contestLabel: '',
  maxItems: '',
  conventionFieldCode: '',
  conventionValue: '',
  valueMin: '',
  valueMax: '',
}

function num(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

function text(s: string): string | null {
  const t = s.trim()
  return t ? t : null
}

function toCriteria(f: CriteriaForm): BatchCriteria {
  return {
    departmentId: num(f.departmentId),
    productId: num(f.productId),
    customerSegment: text(f.customerSegment),
    contractType: text(f.contractType),
    periodFrom: text(f.periodFrom),
    periodTo: text(f.periodTo),
    priority: f.priority,
    contestId: num(f.contestId),
    contestLabel: text(f.contestLabel),
    maxItems: num(f.maxItems),
    conventionFieldCode: text(f.conventionFieldCode),
    conventionValue: text(f.conventionValue),
    valueMin: num(f.valueMin),
    valueMax: num(f.valueMax),
  }
}

function criteriaSummary(c: BatchCriteria, departments: Department[]): string {
  const parts: string[] = []
  if (c.departmentId) parts.push(departments.find(d => d.id === c.departmentId)?.name ?? `settore ${c.departmentId}`)
  if (c.productId) parts.push(`prodotto ${c.productId}`)
  if (c.customerSegment) parts.push(c.customerSegment)
  if (c.contractType) parts.push(`canale ${c.contractType}`)
  if (c.periodFrom || c.periodTo) parts.push(`${c.periodFrom ?? '…'} → ${c.periodTo ?? '…'}`)
  if (c.contestLabel) parts.push(`gara: ${c.contestLabel}`)
  if (c.maxItems) parts.push(`max ${c.maxItems}`)
  if (c.conventionValue) parts.push(`convenzione ${c.conventionValue}`)
  if (c.valueMin != null || c.valueMax != null) parts.push(`valore ${c.valueMin ?? '…'} – ${c.valueMax ?? '…'}`)
  if (c.managerUserId) parts.push(`squadra di ${c.managerUserId}`)
  else if (c.agentIds?.length) parts.push(`${c.agentIds.length} agenti`)
  return parts.length ? parts.join(' · ') : '—'
}

const MAX_BATCH_AGENTS = 50

function parseAgentIds(s: string): number[] {
  return s
    .split(/[\s,;]+/)
    .map(x => Number(x))
    .filter(n => Number.isInteger(n) && n > 0)
}

function CriteriaFields({
  value,
  onChange,
  departments,
}: {
  value: CriteriaForm
  onChange: (v: CriteriaForm) => void
  departments: Department[]
}) {
  const set = <K extends keyof CriteriaForm>(key: K, v: CriteriaForm[K]) => onChange({ ...value, [key]: v })
  return (
    <FormGrid columns={4}>
      <div>
        <Select label="Settore" variant="outlined" value={value.departmentId} onChange={e => set('departmentId', e.target.value)}>
          <option value="">Tutti</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <TextField label="Prodotto (ID)" inputMode="numeric" value={value.productId} onChange={e => set('productId', e.target.value)} />
      </div>
      <div>
        <TextField label="Segmento" list="batch-segments" value={value.customerSegment} onChange={e => set('customerSegment', e.target.value)} />
        <datalist id="batch-segments">
          <option value="consumer" />
          <option value="business" />
        </datalist>
      </div>
      <div>
        <TextField label="Canale (tipo pratica)" value={value.contractType} onChange={e => set('contractType', e.target.value)} />
      </div>
      <div>
        <TextField label="Periodo dal" type="date" value={value.periodFrom} onChange={e => set('periodFrom', e.target.value)} />
      </div>
      <div>
        <TextField label="Periodo al" type="date" value={value.periodTo} onChange={e => set('periodTo', e.target.value)} />
      </div>
      <div>
        <Select label="Priorità" value={value.priority} onChange={e => set('priority', e.target.value as BatchPriority)}>
          {(Object.entries(BATCH_PRIORITY_LABELS) as [BatchPriority, string][]).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <TextField label="Quantità massima" inputMode="numeric" value={value.maxItems} onChange={e => set('maxItems', e.target.value)} />
      </div>
      <div>
        <TextField label="Gara (ID)" inputMode="numeric" value={value.contestId} onChange={e => set('contestId', e.target.value)} />
      </div>
      <div>
        <TextField label="Convenzione/accordo: campo (codice)" maxLength={100} value={value.conventionFieldCode} onChange={e => set('conventionFieldCode', e.target.value)} />
      </div>
      <div>
        <TextField label="Convenzione/accordo: valore" maxLength={100} value={value.conventionValue} onChange={e => set('conventionValue', e.target.value)} />
      </div>
      <div>
        <TextField label="Valore economico da (€)" inputMode="decimal" value={value.valueMin} onChange={e => set('valueMin', e.target.value)} />
      </div>
      <div>
        <TextField label="Valore economico a (€)" inputMode="decimal" value={value.valueMax} onChange={e => set('valueMax', e.target.value)} />
      </div>
      <div className="md-span-3">
        <TextField label="Etichetta gara (max 100 caratteri)" maxLength={100} value={value.contestLabel} onChange={e => set('contestLabel', e.target.value)} />
      </div>
    </FormGrid>
  )
}

// ───────────────────────────────────────────────────────────────────────────────────────────────
// F-078 (REQ-008-003/REQ-009-001): controlli del lotto — si mostrano, non bloccano.
// ───────────────────────────────────────────────────────────────────────────────────────────────

const COLOR_TONE: Record<'Rosso' | 'Giallo' | 'Verde', PillTone> = {
  Rosso: 'error',
  Giallo: 'warning',
  Verde: 'success',
}

export function BatchChecksPanel({ batchId }: { batchId: number }) {
  const [checks, setChecks] = useState<BatchChecks | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    batchesApi
      .checks(batchId)
      .then(result => {
        if (!cancelled) setChecks(result)
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [batchId])

  if (loading) return <LoadingState label="Controlli in corso…" />
  if (error) return <Callout tone="error">Non è stato possibile eseguire i controlli. {error}</Callout>
  if (!checks) return null
  if (checks.itemCount === 0) return <div className="md-typescale-body-small py-2">Nessuna pratica da controllare.</div>

  return (
    <Card variant="outlined" padding="sm">
      <div className="md-cluster">
        <strong>Controlli del lotto</strong>
        <span>{checks.itemCount} pratiche</span>
        <span className="md-cluster">
          <StatusPill tone="success">{checks.verde} verdi</StatusPill>
          <StatusPill tone="warning">{checks.giallo} gialle</StatusPill>
          <StatusPill tone="error">{checks.rosso} rosse</StatusPill>
        </span>
        <span>
          Anomalie: {checks.anomalyCount} su {checks.itemsWithAnomalies} pratiche
          {checks.blockingAnomalyCount > 0 && ` (${checks.blockingAnomalyCount} bloccanti)`}
        </span>
        <span>Agenti coinvolti: {checks.agentIds.length}</span>
      </div>
      <div className="md-typescale-body-small">
        I controlli sono informativi: non impediscono l’invio né l’approvazione del lotto.
      </div>
      {Object.keys(checks.byKind).length > 0 && (
        <div className="md-cluster">
          {(Object.entries(checks.byKind) as [keyof typeof BATCH_ANOMALY_LABELS, number][]).map(([kind, n]) => (
            <StatusPill key={kind} tone="neutral">
              {BATCH_ANOMALY_LABELS[kind] ?? kind}: {n}
            </StatusPill>
          ))}
        </div>
      )}
      <DataTable variant="plain" aria-label="Controlli per pratica">
        <thead>
          <tr>
            <th>Pratica</th>
            <th>Agente</th>
            <th>Completezza</th>
            <th>Anomalie</th>
          </tr>
        </thead>
        <tbody>
          {checks.items.map(row => (
            <tr key={row.contractId}>
              <td className="font-monospace">{row.referenceCode}</td>
              <td>{row.agentId ?? '—'}</td>
              <td>
                <StatusPill tone={COLOR_TONE[row.color]}>{row.color}</StatusPill> {row.scorePercent}%
              </td>
              <td>
                {row.anomalies.length === 0
                  ? '—'
                  : (
                    <span className="md-cluster">
                      {row.anomalies.map((a, i) => (
                        <StatusPill key={i} tone={a.severity === 'Bloccante' ? 'error' : 'warning'}>
                          {BATCH_ANOMALY_LABELS[a.kind] ?? a.kind}: {a.label}
                        </StatusPill>
                      ))}
                    </span>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  )
}

// ───────────────────────────────────────────────────────────────────────────────────────────────
// F-078 (REQ-008-003/REQ-060-002): regole di assegnazione automatica con anteprima ed esecuzione.
// ───────────────────────────────────────────────────────────────────────────────────────────────

interface RuleForm {
  name: string
  priority: string
  isActive: boolean
  operatorId: string
  criteria: CriteriaForm
  agentIds: string
  managerUserId: string
  verifiedFrom: string
  verifiedTo: string
  targetMode: BatchRuleTargetMode
  targetBatchId: string
  reason: string
}

const EMPTY_RULE: RuleForm = {
  name: '',
  priority: '100',
  isActive: true,
  operatorId: '',
  criteria: EMPTY_CRITERIA,
  agentIds: '',
  managerUserId: '',
  verifiedFrom: '',
  verifiedTo: '',
  targetMode: 'NuovoLotto',
  targetBatchId: '',
  reason: '',
}

function ruleToForm(r: BatchRule): RuleForm {
  return {
    name: r.name,
    priority: String(r.priority),
    isActive: r.isActive,
    operatorId: r.operatorId ? String(r.operatorId) : '',
    criteria: {
      departmentId: r.departmentId ? String(r.departmentId) : '',
      productId: r.productId ? String(r.productId) : '',
      customerSegment: r.customerSegment ?? '',
      contractType: r.contractType ?? '',
      periodFrom: r.periodFrom ?? '',
      periodTo: r.periodTo ?? '',
      priority: r.batchPriority,
      contestId: r.contestId ? String(r.contestId) : '',
      contestLabel: r.contestLabel ?? '',
      maxItems: r.maxItems ? String(r.maxItems) : '',
      conventionFieldCode: r.conventionFieldCode ?? '',
      conventionValue: r.conventionValue ?? '',
      valueMin: r.valueMin != null ? String(r.valueMin) : '',
      valueMax: r.valueMax != null ? String(r.valueMax) : '',
    },
    agentIds: (r.agentIds ?? []).join(', '),
    managerUserId: r.managerUserId ? String(r.managerUserId) : '',
    verifiedFrom: r.verifiedFrom ?? '',
    verifiedTo: r.verifiedTo ?? '',
    targetMode: r.targetMode,
    targetBatchId: r.targetBatchId ? String(r.targetBatchId) : '',
    reason: r.reason ?? '',
  }
}

function formToRule(f: RuleForm): BatchRuleInput {
  const c = toCriteria(f.criteria)
  const agents = f.agentIds
    .split(/[\s,;]+/)
    .map(s => Number(s))
    .filter(n => Number.isFinite(n) && n > 0)
  return {
    name: f.name.trim(),
    priority: num(f.priority) ?? 100,
    isActive: f.isActive,
    operatorId: num(f.operatorId),
    departmentId: c.departmentId ?? null,
    productId: c.productId ?? null,
    customerSegment: c.customerSegment ?? null,
    contractType: c.contractType ?? null,
    periodFrom: c.periodFrom ?? null,
    periodTo: c.periodTo ?? null,
    batchPriority: c.priority ?? 'Normale',
    contestId: c.contestId ?? null,
    contestLabel: c.contestLabel ?? null,
    maxItems: c.maxItems ?? null,
    conventionFieldCode: c.conventionFieldCode ?? null,
    conventionValue: c.conventionValue ?? null,
    valueMin: c.valueMin ?? null,
    valueMax: c.valueMax ?? null,
    agentIds: agents.length ? agents : null,
    managerUserId: num(f.managerUserId),
    verifiedFrom: text(f.verifiedFrom),
    verifiedTo: text(f.verifiedTo),
    targetMode: f.targetMode,
    targetBatchId: f.targetMode === 'LottoEsistente' ? num(f.targetBatchId) : null,
    reason: text(f.reason),
  }
}

function RulesTab({ departments, operators, isAdmin }: { departments: Department[]; operators: Operator[]; isAdmin: boolean }) {
  const pickerOn = useFeatureFlag('utenti.selettore')
  const confirm = useConfirm()
  const [rules, setRules] = useState<BatchRule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState<RuleForm>(EMPTY_RULE)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<BatchRuleApplyResult | null>(null)
  const [applying, setApplying] = useState<'preview' | 'run' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setRules(await batchesApi.listRules())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function openNew() {
    setEditingId('new')
    setForm(EMPTY_RULE)
    setError('')
  }

  function openEdit(rule: BatchRule) {
    setEditingId(rule.id)
    setForm(ruleToForm(rule))
    setError('')
  }

  // F-100: dopo "Clona" la copia (inattiva) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(rules, r => openEdit(r))

  async function save() {
    setSaving(true)
    setError('')
    try {
      const body = formToRule(form)
      if (editingId === 'new') await batchesApi.createRule(body)
      else if (editingId !== null) await batchesApi.updateRule(editingId, body)
      setEditingId(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function apply(dryRun: boolean) {
    if (!dryRun && !(await confirm({ title: 'Applicare le regole?', message: 'Applicare le regole? Le pratiche Verificate corrispondenti entreranno nei lotti.', confirmLabel: 'Applica' }))) return
    setApplying(dryRun ? 'preview' : 'run')
    setError('')
    try {
      setResult(await batchesApi.applyRules(dryRun))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante l’applicazione delle regole')
    } finally {
      setApplying(null)
    }
  }

  const setField = <K extends keyof RuleForm>(key: K, v: RuleForm[K]) => setForm(prev => ({ ...prev, [key]: v }))

  return (
    <div className="md-stack">
      <div className="md-cluster justify-content-between">
        <h2 className="md-typescale-title-medium m-0">{rules.length} regole di assegnazione</h2>
        <div className="md-cluster">
          <Button type="button" variant="outlined" size="sm" disabled={applying !== null} onClick={() => void apply(true)}>
            {applying === 'preview' ? 'Anteprima…' : 'Anteprima'}
          </Button>
          <Button type="button" variant="tonal" size="sm" disabled={applying !== null} onClick={() => void apply(false)}>
            {applying === 'run' ? 'Esecuzione…' : 'Esegui'}
          </Button>
          <Button type="button" variant="outlined" size="sm" onClick={openNew}>
            Nuova regola
          </Button>
        </div>
      </div>

      <Callout tone="info">
        Le regole si valutano per priorità crescente: ogni pratica Verificata va alla prima regola che combacia e ha ancora posto.
        Un lotto raggruppa un solo operatore, che si ricava dal prodotto della pratica: le pratiche senza prodotto restano fuori.
        L’esecuzione crea e riempie i lotti ma non li invia per approvazione.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {cloneNotice && (
        <Callout
          tone="info"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setCloneNotice('')} />}
        >
          {cloneNotice}
        </Callout>
      )}

      {result && (
        <Card variant="outlined">
          <CardHeader
            title={result.dryRun ? 'Anteprima (nessuna scrittura)' : 'Esito dell’esecuzione'}
            actions={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setResult(null)} />}
          />
          <CardContent>
            <div className="md-stack">
              <p className="m-0">
                Pratiche Verificate: {result.candidates} · assegnate: {result.assigned} · lotti {result.dryRun ? 'da creare' : 'creati'}:{' '}
                {result.createdBatches} · scartate: {result.discarded.length}
              </p>
              {result.batches.length > 0 && (
                <DataTable variant="plain" aria-label="Lotti dell’esito">
                  <thead>
                    <tr>
                      <th>Regola</th>
                      <th>Lotto</th>
                      <th>Operatore</th>
                      <th>Pratiche</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.batches.map(b => (
                      <tr key={b.slot}>
                        <td>{b.ruleName}</td>
                        <td className="font-monospace">{b.batchCode ?? (b.existingBatchId ? `lotto ${b.existingBatchId}` : 'nuovo')}</td>
                        <td>{b.operatorId ?? '—'}</td>
                        <td>{b.contractIds.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              )}
              {result.discarded.length > 0 && (
                <DataTable variant="plain" aria-label="Pratiche scartate">
                  <thead>
                    <tr>
                      <th>Pratica scartata</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.discarded.map(d => (
                      <tr key={d.contractId}>
                        <td className="font-monospace">{d.referenceCode}</td>
                        <td>{d.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {editingId !== null && (
        <Card variant="outlined">
          <CardHeader title={editingId === 'new' ? 'Nuova regola' : `Modifica regola ${editingId}`} />
          <CardContent>
            <FormGrid columns={4} className="mb-3">
              <div className="md-span-2">
                <TextField label="Nome" maxLength={100} value={form.name} onChange={e => setField('name', e.target.value)} />
              </div>
              <div>
                <TextField label="Priorità (ordine)" inputMode="numeric" value={form.priority} onChange={e => setField('priority', e.target.value)} />
              </div>
              <div>
                <Select label="Operatore" value={form.operatorId} onChange={e => setField('operatorId', e.target.value)}>
                  <option value="">Qualsiasi</option>
                  {operators.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="d-flex align-items-end">
                <Checkbox id="rule-active" label="Attiva" checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} />
              </div>
            </FormGrid>

            <CriteriaFields value={form.criteria} onChange={c => setField('criteria', c)} departments={departments} />

            <FormGrid columns={4} className="mt-3">
              <div className="md-span-2">
                {pickerOn ? (
                  <UserIdsPicker multiple label="Agenti (ID separati da virgola)" value={form.agentIds} onChange={v => setField('agentIds', v)} />
                ) : (
                  <TextField label="Agenti (ID separati da virgola)" value={form.agentIds} onChange={e => setField('agentIds', e.target.value)} />
                )}
              </div>
              <div>
                {pickerOn ? (
                  <UserIdsPicker label="Oppure squadra di (ID utente)" value={form.managerUserId} onChange={v => setField('managerUserId', v)} />
                ) : (
                  <TextField label="Oppure squadra di (ID utente)" inputMode="numeric" value={form.managerUserId} onChange={e => setField('managerUserId', e.target.value)} />
                )}
              </div>
              <div>
                <TextField label="Verificata dal" type="date" value={form.verifiedFrom} onChange={e => setField('verifiedFrom', e.target.value)} />
              </div>
              <div>
                <TextField label="Verificata al" type="date" value={form.verifiedTo} onChange={e => setField('verifiedTo', e.target.value)} />
              </div>
              <div>
                <Select label="Destinazione" value={form.targetMode} onChange={e => setField('targetMode', e.target.value as BatchRuleTargetMode)}>
                  <option value="NuovoLotto">Nuovo lotto (uno per operatore)</option>
                  <option value="LottoEsistente">Lotto esistente</option>
                </Select>
              </div>
              {form.targetMode === 'LottoEsistente' && (
                <div>
                  <TextField label="Lotto (ID)" inputMode="numeric" value={form.targetBatchId} onChange={e => setField('targetBatchId', e.target.value)} />
                </div>
              )}
              <div className="md-span-full">
                <TextField label="Motivazione dei lotti creati" maxLength={1024} value={form.reason} onChange={e => setField('reason', e.target.value)} />
              </div>
            </FormGrid>
          </CardContent>
          <CardActions align="start">
            <Button type="button" variant="tonal" size="sm" disabled={saving} onClick={() => void save()}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
            <Button type="button" variant="text" size="sm" onClick={() => setEditingId(null)}>
              Annulla
            </Button>
          </CardActions>
        </Card>
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable stickyHeader aria-label="Regole di assegnazione">
          <thead>
            <tr>
              <th>Ordine</th>
              <th>Nome</th>
              <th>Criteri</th>
              <th>Operatore</th>
              <th>Agenti / squadra</th>
              <th>Destinazione</th>
              <th>Stato</th>
              <th className="md-num">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <DataTableEmptyRow colSpan={8} icon="rule" title="Nessuna regola definita" />
            ) : rules.map(r => (
              <tr key={r.id}>
                <td>{r.priority}</td>
                <td className="md-typescale-emphasized-body-medium">{r.name}</td>
                <td className="md-typescale-body-small">
                  {criteriaSummary(
                    {
                      departmentId: r.departmentId ?? null,
                      productId: r.productId ?? null,
                      customerSegment: r.customerSegment ?? null,
                      contractType: r.contractType ?? null,
                      periodFrom: r.periodFrom ?? null,
                      periodTo: r.periodTo ?? null,
                      contestLabel: r.contestLabel ?? null,
                      maxItems: r.maxItems ?? null,
                      conventionValue: r.conventionValue ?? null,
                      valueMin: r.valueMin ?? null,
                      valueMax: r.valueMax ?? null,
                    },
                    departments,
                  )}
                </td>
                <td>{operators.find(o => o.id === r.operatorId)?.name ?? r.operatorId ?? '—'}</td>
                <td>{r.managerUserId ? `squadra di ${r.managerUserId}` : r.agentIds?.length ? `${r.agentIds.length} agenti` : 'tutti'}</td>
                <td>{r.targetMode === 'LottoEsistente' ? `lotto ${r.targetBatchId}` : 'nuovo lotto'}</td>
                <td>
                  <StatusPill tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Attiva' : 'Disattiva'}</StatusPill>
                </td>
                <td className="md-num text-nowrap">
                  <div className="md-cluster justify-content-end">
                    <CloneConfigurationButton
                      entityLabel="regola di assegnazione"
                      url={`/api/batches/rules/${r.id}/clone`}
                      defaultName={r.name}
                      note="Si copiano criteri, operatore, squadra e destinazione. La copia nasce disattiva."
                      onCloned={async id => { await load(); setCloneNotice(CLONE_NOTICE); openAfterClone(id) }}
                    />
                    <Button type="button" variant="outlined" size="sm" onClick={() => openEdit(r)}>
                      Modifica
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {isAdmin && <RuleLifecyclePanel family="batch-assignment" onChanged={() => void load()} />}
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────────────────────────────
// Pagina
// ───────────────────────────────────────────────────────────────────────────────────────────────

/**
 * REQ-003-001: full read-only lotti list, filterable across all three BatchStatus values — the
 * sidebar's missing "Distinte" entry. See BatchesController.cs's GET (list) and GET/{id}.
 *
 * REQ-008-004 ("l'amministratore deve sempre poter spostare manualmente una pratica da un lotto
 * all'altro prima dell'invio", spec §8) adds the assembly-phase management this page was missing:
 * expanding a row (batchesApi.get) to see its items, a per-item "Rimuovi" action
 * (batchesApi.removeItem — BatchesController.RemoveItem already implements the move's "remove"
 * half; the "move" half is completed by re-assigning the freed practice to a different lotto from
 * ContractsPage.tsx, whose handleAssignToBatch now offers picking one of these same not-yet-submitted
 * lotti) and a per-batch "Invia per approvazione" action (batchesApi.submit). Both actions are only
 * ever shown for a batch with SubmittedAt == null — see Batch.EnsureAssemblyPhase's remarks for why
 * that field, not BatchStatus, is the assembly-phase signal; the backend re-enforces the same guard,
 * this is purely about not offering an action the API would already refuse. This still doesn't
 * duplicate or replace ApprovalsPage.tsx's pending-approval workflow: that page keeps owning
 * Approve/Reject, which only ever apply after SubmittedAt is set.
 *
 * F-078 (TASK-0332): criteri di porzionamento alla creazione, scheda "Regole" con anteprima ed
 * esecuzione, pannello dei controlli (anomalie e readiness) e "Revoca" di un lotto approvato.
 * `/batches?id=N` apre direttamente il lotto N (è il link delle notifiche).
 */
export function BatchesPage({ auth }: { auth: LoginResponse }) {
  const [searchParams] = useSearchParams()
  const linkedId = Number(searchParams.get('id')) || null

  const [tab, setTab] = useState<'lotti' | 'regole'>('lotti')
  const [status, setStatus] = useState<BatchStatus | ''>('')
  const [page, setPage] = useState(1)
  // F-094 (REQ-277-001): l'ordine lo decide il server; '' = quello di sempre (più recenti prima).
  const [sort, setSort] = useState<BatchSort | ''>('')

  const [items, setItems] = useState<Batch[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  // REQ-008-004: which row (if any) is expanded to show its items, plus that row's own
  // load/action state. Kept separate from the list's own loading/error state, same separation
  // ApprovalsPage.tsx keeps between `loading`/`loadError` and `busyId`/`actionError`.
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [expandedItems, setExpandedItems] = useState<BatchItem[]>([])
  const [expandedLoading, setExpandedLoading] = useState(false)
  const [expandedError, setExpandedError] = useState('')
  const [busyItemId, setBusyItemId] = useState<number | null>(null)
  const [submittingId, setSubmittingId] = useState<number | null>(null)
  const [actionError, setActionError] = useState('')

  // F-078
  const [departments, setDepartments] = useState<Department[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [showNew, setShowNew] = useState(false)
  const [newOperatorId, setNewOperatorId] = useState('')
  const [newReason, setNewReason] = useState('')
  const [newCriteria, setNewCriteria] = useState<CriteriaForm>(EMPTY_CRITERIA)
  const [newAgentIds, setNewAgentIds] = useState('')
  const [newManagerUserId, setNewManagerUserId] = useState('')
  const [creating, setCreating] = useState(false)
  const [checksFor, setChecksFor] = useState<number | null>(null)
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revokeBusy, setRevokeBusy] = useState(false)
  const [revokeError, setRevokeError] = useState('')

  useEffect(() => {
    void fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    void fetchOperators().then(r => setOperators(r.items)).catch(() => setOperators([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const result = await batchesApi.list({ status: status || undefined, page, pageSize: PAGE_SIZE, sort: sort || undefined })
      setItems(result.items)
      setTotal(result.total)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [status, page, sort])

  useEffect(() => {
    void load()
  }, [load])

  /** F-094: clic su un'intestazione = ordina per quella colonna; un secondo clic inverte (o torna all'ordine di sempre). */
  function toggleSort(column: 'created' | 'status') {
    setSort(prev => {
      if (column === 'created') return prev === 'created_asc' ? 'created_desc' : 'created_asc'
      return prev === 'status' ? '' : 'status'
    })
    setPage(1)
  }

  function handleStatusChange(value: string) {
    setStatus(value as BatchStatus | '')
    setPage(1)
  }

  // REQ-008-004
  const toggleExpand = useCallback(async (id: number) => {
    setChecksFor(null)
    if (expandedId === id) {
      setExpandedId(null)
      return
    }
    setExpandedId(id)
    setExpandedItems([])
    setExpandedError('')
    setExpandedLoading(true)
    try {
      const result = await batchesApi.get(id)
      setExpandedItems(result.items)
    } catch (e) {
      setExpandedError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setExpandedLoading(false)
    }
  }, [expandedId])

  // F-078: il link delle notifiche (/batches?id=N) apre il lotto N, una volta sola.
  const [linkedOpened, setLinkedOpened] = useState(false)
  useEffect(() => {
    if (linkedId && !linkedOpened) {
      setLinkedOpened(true)
      void toggleExpand(linkedId)
      setChecksFor(linkedId)
    }
  }, [linkedId, linkedOpened, toggleExpand])

  // REQ-008-004: BatchesController.RemoveItem reverts the practice to Verificata (spec §8's
  // "spostare... prima dell'invio" — the freed contract can then be assigned to a different lotto
  // from ContractsPage.tsx). Refuses with 400 once the batch is no longer in the assembly phase;
  // this button is only ever rendered while it still is, but the error is still surfaced if the
  // state changed underneath (e.g. someone else just submitted it).
  async function handleRemoveItem(batchId: number, contractId: number) {
    setActionError('')
    setBusyItemId(contractId)
    try {
      await batchesApi.removeItem(batchId, contractId)
      const result = await batchesApi.get(batchId)
      setExpandedItems(result.items)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Errore durante la rimozione della pratica')
    } finally {
      setBusyItemId(null)
    }
  }

  // REQ-008-004: the missing caller of batchesApi.submit — until this, no batch ever got
  // SubmittedAt set, so ApprovalsPage.tsx (which filters on !!b.submittedAt) was permanently empty.
  async function handleSubmit(id: number) {
    setActionError('')
    setSubmittingId(id)
    try {
      await batchesApi.submit(id)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Errore durante l’invio per approvazione')
    } finally {
      setSubmittingId(null)
    }
  }

  // F-078 (REQ-008-003): crea un lotto con i criteri di porzionamento.
  async function handleCreate() {
    setActionError('')
    const agents = parseAgentIds(newAgentIds)
    const manager = num(newManagerUserId)
    if (agents.length > MAX_BATCH_AGENTS) {
      setActionError(`Gli agenti di un lotto sono al massimo ${MAX_BATCH_AGENTS}.`)
      return
    }
    if (agents.length > 0 && manager) {
      setActionError('Indica gli agenti oppure la squadra di un responsabile, non entrambi.')
      return
    }
    setCreating(true)
    try {
      await batchesApi.create(num(newOperatorId) ?? undefined, text(newReason) ?? undefined, {
        ...toCriteria(newCriteria),
        agentIds: agents.length ? agents : null,
        managerUserId: manager,
      })
      setShowNew(false)
      setNewOperatorId('')
      setNewReason('')
      setNewCriteria(EMPTY_CRITERIA)
      setNewAgentIds('')
      setNewManagerUserId('')
      setPage(1)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Errore durante la creazione del lotto')
    } finally {
      setCreating(false)
    }
  }

  // F-078 (REQ-009-005): revoca dell'approvazione prima della fase operatore (solo Manager).
  async function handleRevoke() {
    if (revokingId === null) return
    if (!revokeReason.trim()) {
      setRevokeError('La motivazione della revoca è obbligatoria.')
      return
    }
    setRevokeBusy(true)
    setRevokeError('')
    try {
      await batchesApi.revoke(revokingId, revokeReason.trim())
      setRevokingId(null)
      setRevokeReason('')
      await load()
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : 'Errore durante la revoca')
    } finally {
      setRevokeBusy(false)
    }
  }

  return (
    <Page>
      <PageHeader
        title="Distinte"
        icon="inventory"
        tabs={
          <Tabs
            aria-label="Sezioni distinte"
            items={[{ id: 'lotti', label: 'Lotti' }, { id: 'regole', label: 'Regole' }]}
            value={tab}
            onChange={id => setTab(id === 'regole' ? 'regole' : 'lotti')}
          />
        }
      />

      {tab === 'regole' ? (
        <RulesTab departments={departments} operators={operators} isAdmin={auth.role === 'Administrator' || auth.role === 'EqualAdmin'} />
      ) : (
        <>
          <div className="md-cluster justify-content-between">
            <h2 className="md-typescale-title-medium m-0">{total} lotti</h2>
            <Button type="button" variant="outlined" size="sm" onClick={() => setShowNew(v => !v)}>
              {showNew ? 'Chiudi' : 'Nuovo lotto'}
            </Button>
          </div>

          {actionError && <Callout tone="error">{actionError}</Callout>}

          {showNew && (
            <Card variant="outlined">
              <CardHeader title="Nuovo lotto" />
              <CardContent>
                <FormGrid columns={3} className="mb-3">
                  <div>
                    <Select label="Operatore" value={newOperatorId} onChange={e => setNewOperatorId(e.target.value)}>
                      <option value="">Si fissa con la prima pratica</option>
                      {operators.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="md-span-2">
                    <TextField label="Motivazione" maxLength={1024} value={newReason} onChange={e => setNewReason(e.target.value)} />
                  </div>
                </FormGrid>
                <CriteriaFields value={newCriteria} onChange={setNewCriteria} departments={departments} />
                <FormGrid columns={3} className="mt-3">
                  <div className="md-span-2">
                    <TextField
                      label="Agenti (ID separati da virgola, al massimo 50)"
                      value={newAgentIds}
                      onChange={e => setNewAgentIds(e.target.value)}
                    />
                  </div>
                  <div>
                    <TextField
                      label="Oppure squadra di (ID utente)"
                      inputMode="numeric"
                      value={newManagerUserId}
                      onChange={e => setNewManagerUserId(e.target.value)}
                    />
                  </div>
                </FormGrid>
                <div className="md-typescale-body-small mt-2">
                  Un lotto con criteri accetta solo pratiche che li rispettano, entro la quantità massima.
                </div>
              </CardContent>
              <CardActions align="start">
                <Button type="button" variant="tonal" size="sm" disabled={creating} onClick={() => void handleCreate()}>
                  {creating ? 'Creazione…' : 'Crea lotto'}
                </Button>
              </CardActions>
            </Card>
          )}

          <FilterBar>
            <Select label="Stato" value={status} onChange={e => handleStatusChange(e.target.value)}>
              <option value="">Tutti</option>
              {(Object.entries(BATCH_STATUS_LABELS) as [BatchStatus, string][]).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FilterBar>

          {loading ? (
            <LoadingState />
          ) : loadError ? (
            <Callout tone="error">
              Non è stato possibile caricare i lotti. {loadError}
            </Callout>
          ) : (
            <>
              <DataTable stickyHeader aria-label="Lotti">
                <thead>
                  <tr>
                    <SortHeader
                      label="Codice lotto"
                      sortKey="created"
                      activeKey={sort === '' || sort === 'created_asc' || sort === 'created_desc' ? 'created' : null}
                      direction={sort === 'created_asc' ? 'asc' : 'desc'}
                      onSort={() => toggleSort('created')}
                    />
                    <SortHeader
                      label="Stato"
                      sortKey="status"
                      activeKey={sort === 'status' ? 'status' : null}
                      direction="asc"
                      onSort={() => toggleSort('status')}
                    />
                    <th>Operatore</th>
                    <th>Criteri</th>
                    <th>Priorità</th>
                    <th>Sottomesso il</th>
                    <th>Approvato il</th>
                    <th>Motivazione rifiuto</th>
                    <th className="md-num">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <DataTableEmptyRow colSpan={9} icon="inventory" title="Nessun lotto trovato" />
                  )}
                  {items.map(b => {
                    // REQ-008-004: the assembly-phase signal is SubmittedAt == null (see
                    // Batch.EnsureAssemblyPhase's remarks), not b.status — a submitted-but-not-yet-
                    // decided lotto is still 'InAttesaApprovazione'.
                    const isAssemblyPhase = !b.submittedAt
                    const isExpanded = expandedId === b.id
                    return (
                      <Fragment key={b.id}>
                        <tr className={linkedId === b.id ? 'table-active' : undefined}>
                          <td className="md-typescale-emphasized-body-medium font-monospace">
                            {b.batchCode}
                            {b.createdByRuleId ? <StatusPill tone="info" className="ms-1">da regola</StatusPill> : null}
                          </td>
                          <td>
                            <StatusPill tone={pillTone(BATCH_STATUS_COLORS[b.status])}>
                              {BATCH_STATUS_LABELS[b.status] ?? b.status}
                            </StatusPill>
                            {b.revokedAt && !b.approvedAt ? (
                              <StatusPill tone="neutral" className="ms-1" title={b.revocationReason ?? undefined}>
                                revocato
                              </StatusPill>
                            ) : null}
                          </td>
                          <td>{operators.find(o => o.id === b.operatorId)?.name ?? b.operatorId ?? '—'}</td>
                          <td className="md-typescale-body-small">{criteriaSummary(b, departments)}</td>
                          <td>{b.priority ? (BATCH_PRIORITY_LABELS[b.priority] ?? b.priority) : '—'}</td>
                          <td>{fmtDateTime(b.submittedAt)}</td>
                          <td>{fmtDateTime(b.approvedAt)}</td>
                          <td>{b.rejectionReason ?? '—'}</td>
                          <td className="md-num text-nowrap">
                            <div className="md-cluster justify-content-end">
                              <Button
                                type="button"
                                variant="outlined"
                                size="sm"
                                onClick={() => void toggleExpand(b.id)}
                              >
                                {isExpanded ? 'Chiudi' : 'Dettagli'}
                              </Button>
                              {isAssemblyPhase && (
                                <Button
                                  type="button"
                                  variant="tonal"
                                  size="sm"
                                  disabled={submittingId === b.id}
                                  onClick={() => void handleSubmit(b.id)}
                                >
                                  {submittingId === b.id ? 'Invio…' : 'Invia per approvazione'}
                                </Button>
                              )}
                              {b.status === 'Approvato' && (
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="sm"
                                  onClick={() => {
                                    setRevokingId(b.id)
                                    setRevokeReason('')
                                    setRevokeError('')
                                  }}
                                >
                                  Revoca
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9}>
                              {b.revokedAt && !b.approvedAt && (
                                <Callout tone="info">
                                  Approvazione revocata il {fmtDateTime(b.revokedAt)}: {b.revocationReason ?? '—'}
                                </Callout>
                              )}
                              {expandedLoading ? (
                                <LoadingState />
                              ) : expandedError ? (
                                <Callout tone="error">{expandedError}</Callout>
                              ) : expandedItems.length === 0 ? (
                                <div className="md-typescale-body-medium py-2">Nessuna pratica in questo lotto.</div>
                              ) : (
                                <div className="md-stack">
                                  <DataTable aria-label="Pratiche del lotto">
                                    <thead>
                                      <tr>
                                        <th>Pratica (Contract ID)</th>
                                        <th>Aggiunta il</th>
                                        {isAssemblyPhase && <th className="md-num">Azioni</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedItems.map(it => (
                                        <tr key={it.id}>
                                          <td>{it.contractId}</td>
                                          <td>{fmtDateTime(it.createdAt)}</td>
                                          {isAssemblyPhase && (
                                            <td className="md-num">
                                              <Button
                                                type="button"
                                                variant="outlined"
                                                size="sm"
                                                disabled={busyItemId === it.contractId}
                                                onClick={() => void handleRemoveItem(b.id, it.contractId)}
                                              >
                                                {busyItemId === it.contractId ? 'Rimozione…' : 'Rimuovi'}
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </DataTable>
                                  {checksFor === b.id ? (
                                    <BatchChecksPanel batchId={b.id} />
                                  ) : (
                                    <div>
                                      <Button type="button" variant="outlined" size="sm" onClick={() => setChecksFor(b.id)}>
                                        Esegui i controlli
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </DataTable>
              {Math.ceil(total / PAGE_SIZE) > 1 && (
                <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
              )}
            </>
          )}
        </>
      )}

      <Dialog
        open={revokingId !== null}
        onClose={() => setRevokingId(null)}
        title="Revoca approvazione del lotto"
        supportingText="Il lotto torna in attesa di approvazione e le sue pratiche tornano in “Approvazione richiesta”. Si può revocare solo finché nessuna pratica è entrata nella fase operatore."
        as="form"
        onSubmit={e => { e.preventDefault(); void handleRevoke() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setRevokingId(null)}>
              Annulla
            </Button>
            <Button type="submit" variant="text" tone="error" disabled={revokeBusy}>
              Conferma revoca
            </Button>
          </>
        }
      >
        {revokeError && <Callout tone="error">{revokeError}</Callout>}
        <FormGrid columns={1}>
          <TextField label="Motivazione (obbligatoria)" multiline rows={3} value={revokeReason} onChange={e => setRevokeReason(e.target.value)} />
        </FormGrid>
      </Dialog>
    </Page>
  )
}
